import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container } from 'rsuite'

import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import { ThickSelectPicker } from '../../ui_reusable/StandardSelectPickers'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { TagFilledActive, TagFilledInactive } from '../../ui_reusable/Tags'

import ProjectOverview from './project/ProjectOverview'
import ProjectUserList from './project/ProjectUserList'
import ProjectDeactivate from './project/ProjectDeactivate'
import ProjectSolutionList from './project/ProjectSolutionList'

import { useClientContext } from '../../../context/ClientContext'
import { useProjectContext } from '../../../context/ProjectContext'
import { projectPickerLabel } from '../../../context/ClientProjectContext'

const ProjectPage = () => {
  const navigate = useNavigate()
  const { project: projectParam } = useParams()
  const id = projectParam
  const [project, setProject] = useState(null)
  const [projectOptions, setProjectOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [refresh, setRefresh] = useState(new Date().getTime())

  const { setSelectedClient } = useClientContext()
  const { setSelectedProject } = useProjectContext()

  useEffect(() => {
    const abortController = new AbortController()

    const getProject = async () => {
      if (!id) return
      setLoading(true)
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const data = await response.json()

        setProject(data.data)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch project:', error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    getProject()

    return () => {
      abortController.abort()
    }
  }, [id, refresh])

  useEffect(() => {
    const abortController = new AbortController()

    const getProjectOptions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/?ordering=name`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const res = await response.json()

        setProjectOptions(res.data || [])
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch project options:', error)
          setProjectOptions([])
        }
      }
    }

    getProjectOptions()

    return () => {
      abortController.abort()
    }
  }, [refresh])

  const handleProjectChange = (nextId) => {
    if (!nextId || String(nextId) === String(id)) return

    const nextProject = projectOptions.find((elem) => elem.id === nextId)
    if (nextProject) {
      setSelectedClient(nextProject.client)
      setSelectedProject({
        id: nextProject.id,
        name: nextProject.name,
        logo: nextProject.logo,
        status: nextProject.status,
      })
    }
    navigate(`/projects/${nextId}`)
  }

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Manage project</SectionTitle>
        {project && project.status === 'active' ? (
          <TagFilledActive label="Project is active" />
        ) : (
          <TagFilledInactive label="Project is inactive" />
        )}
        <ThickSelectPicker
          placeholder="Select project"
          value={project?.id}
          data={projectOptions.map((item) => ({
            label: projectPickerLabel(item.name, item.status),
            value: item.id,
          }))}
          onChange={handleProjectChange}
          style={{ width: 250 }}
        />
      </HeaderContainer>

      <Container style={{ margin: '0 10px' }}>
        {project && !loading ? (
          <SimpleAccordion
            id={'manageProjectPageAccordion'}
            defaultActiveKey={['settings']}
          >
            <SimpleAccordion.Panel
              header={<>Project Settings</>}
              eventKey="settings"
            >
              <ProjectOverview project={project} />
            </SimpleAccordion.Panel>

            <SimpleAccordion.Panel header="Model" eventKey="Model">
              <ProjectSolutionList
                projectId={id}
                disabled={project.status !== 'active'}
              />
            </SimpleAccordion.Panel>

            <SimpleAccordion.Panel header="Project Users" eventKey="users">
              <ProjectUserList projectId={id} />
            </SimpleAccordion.Panel>
            <SimpleAccordion.Panel
              header="Deactivate Project"
              eventKey="deactivate"
              color="#fffcf5"
            >
              <ProjectDeactivate project={project} setRefresh={setRefresh} />
            </SimpleAccordion.Panel>
          </SimpleAccordion>
        ) : (
          <div>Loading project configuration...</div>
        )}
      </Container>
    </>
  )
}

export default ProjectPage
